import i18n from 'i18next';
import { getDateLocale } from '@/utils';
import React, { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { useTranslation } from "react-i18next";
import {
  Bell,
  X,
  CheckCircle,
  AlertCircle,
  Info,
  TrendingUp,
  Calendar,
  Mail,
  Users,
  Target
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { format } from "date-fns";

import { showSuccess, showError, showWarning, showInfo, showComingSoon } from "@/utils/toast";
import { useNavigate } from "react-router-dom";
import { useUser } from "@/hooks/useUser";

const notificationIcons = {
  new_lead: { icon: Users, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  lead_converted: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-500/10' },
  reminder: { icon: Calendar, color: 'text-yellow-500', bg: 'bg-yellow-500/10' },
  email_opened: { icon: Mail, color: 'text-purple-500', bg: 'bg-purple-500/10' },
  follow_up_due: { icon: AlertCircle, color: 'text-orange-500', bg: 'bg-orange-500/10' },
  task_assigned: { icon: Target, color: 'text-cyan-500', bg: 'bg-cyan-500/10' },
  deal_won: { icon: TrendingUp, color: 'text-emerald-500', bg: 'bg-emerald-500/10' }
};

export default function NotificationCenter() {
  const { t } = useTranslation();
  const { user } = useUser();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const notifications = useQuery(api.notifications.get, { limit: 50 }) || [];

  const markAsRead = useMutation(api.notifications.markAsRead);
  const markAllAsRead = useMutation(api.notifications.markAllAsRead);
  const removeNotification = useMutation(api.notifications.remove);

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead({ id: notification._id });
    }
    if (notification.link) {
      navigate(notification.link);
      setOpen(false);
    }
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
    showSuccess(t('app.notifications.mark_all_read'));
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          size="icon"
          className="relative text-gray-400 hover:text-white hover:bg-white/10 bg-transparent border-0"
        >
          <Bell size={20} />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center font-bold animate-pulse">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:w-[400px] bg-dark-cardborder-dark-line text-white">
        <SheetHeader className="border-bborder-dark-line pb-4">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-white">{t('app.notifications.title')}</SheetTitle>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMarkAllRead}
                className="text-xs text-gray-400 hover:text-white"
              >
                {t('app.notifications.mark_all_read')}
              </Button>
            )}
          </div>
        </SheetHeader>

        <div className="mt-4 space-y-2 overflow-y-auto max-h-[calc(100vh-120px)]">
          {notifications.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Bell size={40} className="mx-auto mb-3 opacity-50" />
              <p>{t('app.notifications.no_notifications')}</p>
            </div>
          ) : (
            notifications.map((notification) => {
              const config = notificationIcons[notification.type] || {
                icon: Info,
                color: 'text-gray-500',
                bg: 'bg-gray-500/10'
              };
              const Icon = config.icon;

              return (
                <div
                  key={notification._id}
                  className={`p-4 rounded-lg border transition-all cursor-pointer ${notification.read
                    ? 'bg-dark-elborder-dark-line'
                    : 'bg-red-500/5 border-red-500/20'
                    } hover:border-red-500/50`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${config.bg} shrink-0`}>
                      <Icon size={16} className={config.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-medium text-white text-sm">{notification.title}</h4>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-gray-500 hover:text-red-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeNotification({ id: notification._id });
                          }}
                        >
                          <X size={12} />
                        </Button>
                      </div>
                      <p className="text-sm text-gray-400 mt-1">{notification.message}</p>
                      <p className="text-xs text-gray-600 mt-2">
                        {format(new Date(notification.created_at || Date.now()), 'PPp', { locale: getDateLocale(i18n.language) })}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}